import { forwardRef } from 'react'
import { SignalDot, TextLink } from '@indipia/ui-core'
import {
  SHIP_PHASE_DOT, shipPickButton, shipReasonTexts, shipSecondaryLink, shipStages,
  type ShipDeliveryItem, type ShipRowButton,
} from '../../constants/ship.logic'
import { ShipActionButton } from './ShipActionButton'
import { ShipStageCell } from './ShipStageCell'
import { SHIP_POPUP_TEXT, shipPopupDeliveryLine } from '../../constants/ship.logic'


interface Props {
  item: ShipDeliveryItem
  taskName: string | undefined
  today: string
  highlighted: boolean
  canAssignForeman: boolean
  loading: boolean
  onAction: (item: ShipDeliveryItem, button: Exclude<ShipRowButton, null>) => void
  onPhotoLink: (item: ShipDeliveryItem, kind: 'LOAD' | 'UNLOAD') => void
  /** 대표 버튼 아래 보조 링크(직접 입고 확인) */
  onConfirmReceipt: (item: ShipDeliveryItem) => void
}

/** C-4 한 행 = 좌측 머리(점·공종·거래처·품목·입고일) + 4칸 흐름 + 버튼 1개(+ 보조 링크) + 사유줄. */
export const ShipDeliveryRow = forwardRef<HTMLDivElement, Props>(function ShipDeliveryRow(
  { item, taskName, today, highlighted, canAssignForeman, loading, onAction, onPhotoLink, onConfirmReceipt }, ref,
) {
  const stages = shipStages(item, today)
  const reasons = shipReasonTexts(item, today)
  const button = shipPickButton(item, { canAssignForeman })
  const secondary = shipSecondaryLink(item)
  const hasPhotos = item.availableActions.includes('VIEW_PHOTOS')
  const dim = item.phaseCode === 'NOT_YET'
  const deliveryLine = shipPopupDeliveryLine(item.deliveryDate, today)

  return (
    <div
      ref={ref}
      role="listitem"
      aria-current={highlighted ? 'true' : undefined}
      className={`flex flex-col gap-03 px-05 py-04 border-b border-hairline border-border transition-colors duration-150 ${highlighted ? 'bg-group-subtle' : 'bg-transparent'}`}
    >
      <div className="flex items-start gap-05">
        <div className="flex w-48 min-w-0 shrink-0 flex-col gap-01">
          <span className="flex items-center gap-02">
            <SignalDot color={SHIP_PHASE_DOT[item.phaseCode].tone} label={SHIP_PHASE_DOT[item.phaseCode].label} />
            <span style={{ fontSize: 'var(--type-body-size)', fontWeight: 700, color: dim ? 'var(--shell-on-surface-2)' : 'var(--shell-on-surface)' }}>
              {taskName ?? SHIP_POPUP_TEXT.taskNameMissing}
            </span>
          </span>
          <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', overflowWrap: 'anywhere' }}>{item.vendorName}</span>
          {item.itemSummary && (
            <span style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', overflowWrap: 'anywhere' }}>{item.itemSummary}</span>
          )}
          <span style={{ fontSize: 'var(--type-caption-size)', color: dim ? 'var(--shell-on-surface-2)' : 'var(--shell-on-surface)' }}>{deliveryLine}</span>
        </div>

        <div className="flex min-w-0 flex-1 gap-03">
          {stages.map(st => (
            <ShipStageCell
              key={st.key}
              stage={st}
              onPhotoLink={hasPhotos && (st.key === 'REPLY' || st.key === 'RECEIPT') ? kind => onPhotoLink(item, kind) : undefined}
            />
          ))}
        </div>

        <div className="flex min-w-24 flex-col items-end gap-1">
          <ShipActionButton button={button} loading={loading} onClick={() => { if (button) onAction(item, button) }} />
          {secondary && (
            <TextLink onClick={() => { if (!loading) onConfirmReceipt(item) }}>{secondary.label}</TextLink>
          )}
        </div>
      </div>

      {reasons.length > 0 && (
        <div className="flex flex-col gap-01">
          {reasons.map((t, i) => (
            <span key={i} style={{ fontSize: 'var(--type-caption-size)', color: 'var(--status-danger-600)' }}>{t}</span>
          ))}
        </div>
      )}
    </div>
  )
})
